import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { authApi } from '../services/api'
import Button from '../components/common/Button'
import Input from '../components/common/Input'

/**
 * Forgot Password — public page.
 *
 * User enters their email; if the account exists, the server emails them
 * a reset link. On success we show a confirmation screen so they know to
 * check their inbox.
 *
 * Per the product decision: if the email isn't found, we DO surface that
 * directly (better UX, trade-off accepted given the low-sensitivity
 * 49-user context).
 */
export default function ForgotPassword() {
    const navigate = useNavigate()
    const [email, setEmail] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    async function handleSubmit(e) {
        e.preventDefault()
        if (!email.trim()) {
            toast.error('Email is required')
            return
        }

        setSubmitting(true)
        try {
            const response = await authApi.forgotPassword(email.trim())
            if (response.success) {
                setSubmitted(true)
            } else {
                toast.error(response.message || 'Could not send reset link')
            }
        } catch {
            toast.error('Network error. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    if (submitted) {
        return (
            <Shell>
                <div className="text-center space-y-4">
                    <div className="text-4xl">📧</div>
                    <h2 className="text-xl font-semibold text-ghasc-navy">Check your email</h2>
                    <p className="text-gray-600 text-sm">
                        We sent a password reset link to <strong>{email}</strong>.
                        The link expires in 24 hours.
                    </p>
                    <p className="text-gray-500 text-xs">
                        Didn't get it? Check your spam folder, or wait a minute and try again.
                    </p>
                    <div className="pt-2">
                        <Button variant="secondary" onClick={() => navigate('/login')}>
                            Back to login
                        </Button>
                    </div>
                </div>
            </Shell>
        )
    }

    return (
        <Shell>
            <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="text-xl font-semibold text-ghasc-navy">Forgot your password?</h2>
                <p className="text-sm text-gray-500">
                    Enter your email and we'll send you a link to reset your password.
                </p>

                <Input
                    id="forgot-email"
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                />

                <Button type="submit" loading={submitting} className="w-full">
                    Send reset link
                </Button>

                <p className="text-center text-sm text-gray-500 pt-4">
                    Remembered it?{' '}
                    <Link to="/login" className="text-ghasc-navy hover:underline">
                        Back to login
                    </Link>
                </p>
            </form>
        </Shell>
    )
}

function Shell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ghasc-navy-dark to-ghasc-navy px-4 py-8">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
                <div className="text-center mb-6">
                    <h1 className="text-2xl font-bold text-ghasc-navy">GHASC Literature</h1>
                </div>
                {children}
            </div>
        </div>
    )
}
